import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	VictoryAxis,
	VictoryChart,
	VictoryLegend,
	VictoryLine,
	VictoryScatter,
} from 'victory';
import dayjs from 'dayjs';
import { FuelExpensesPerAssetDashboardDto, FuelExpensesPerAssetMonthDto } from 'api/dashboard/dtos/FuelExpensesPerAssetDashboardDto';
import Utils from 'common/services/Utils';
import { DEFAULT_LANGUAGE, STORAGE } from 'Config';
import ChartLegendLabel from 'common/components/charts/ChartLegendLabel';
import Colors from 'styles/export/colors.module.scss';
import ChartEmptyLabel from 'common/components/charts/ChartEmptyLabel';
import useElementSize from 'common/hooks/useElementSize';
import Storage from 'common/services/Storage';
import GenericTypesService from 'api/genericTypes/GenericTypesService';
import { GenericType } from 'api/genericTypes/enums/GenericType';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import WidgetNoData from '../components/WidgetNoData';
import styles from './FuelExpensesPerAssetChart.module.scss';

// Series palette per docs/COLORS.md (data colors 1–5).
const SERIES_PALETTE = ['#02c7c7', '#038cb6', '#7c5cbf', '#e8873d', '#e05a8a'];

const colorForIndex = (index: number) => SERIES_PALETTE[index % SERIES_PALETTE.length];

type Props = {
	data: FuelExpensesPerAssetDashboardDto;
};

const FuelExpensesPerAssetChart = ({ data }: Props) => {
	const { t } = useTranslation();
	const [hiddenVehicles, setHiddenVehicles] = useState<string[]>([]);
	const [reRenderKey, setReRenderKey] = useState(new Date().getTime());
	const [containerRef, { width, height }] = useElementSize();
	const [tooltip, setTooltip] = useState<{ dateTime: number; clientX: number; clientY: number } | null>(null);
	const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;
	const fuelTypeIdRef = useRef<string | null>(null);

	useEffect(() => {
		setReRenderKey(new Date().getTime());
	}, [data, width, height]);

	useEffect(() => {
		let cancelled = false;
		GenericTypesService.catalogCharge()
			.then(types => {
				if (cancelled) return;
				const fuel = (types || []).find(x => x.label === GenericType.FUEL || x.name === GenericType.FUEL);
				fuelTypeIdRef.current = fuel?.value ?? null;
			})
			.catch(() => { /* ignore */ });
		return () => { cancelled = true; };
	}, []);

	const goToCharges = async (vehicleId: string, registrationNumber: string, date: Date) => {
		const startDate = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
		const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
		let typeId = fuelTypeIdRef.current;
		if (!typeId) {
			try {
				const types = await GenericTypesService.catalogCharge();
				const fuel = (types || []).find(x => x.label === GenericType.FUEL || x.name === GenericType.FUEL);
				typeId = fuel?.value ?? null;
				fuelTypeIdRef.current = typeId;
			} catch {
				// ignore
			}
		}
		try {
			const next: Record<string, unknown> = {
				startDate,
				endDate,
				vehicleId,
				vehicleName: registrationNumber,
				vehiclesInactive: false,
			};
			if (typeId) {
				next.typeId = typeId;
				next.type = GenericType.FUEL;
				next.typeName = t('common.generic_types.FUEL' as any);
			}
			localStorage.setItem('CHARGESFILTER-0', JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/charges', '_blank');
	};

	if (!data.items || data.items.length === 0) {
		return <WidgetNoData />;
	}

	const chartWidth = Math.max(width, 100);
	const legendHeight = 28;
	const chartHeight = Math.max(height, 60);
	const chartPadding = { top: 15, left: 65, right: 25, bottom: 35 + legendHeight };

	const visibleItems = data.items.filter(v => !hiddenVehicles.includes(v.vehicleId));

	const allItems = visibleItems.flatMap(v => v.items);
	const allDates = data.items
		.flatMap(v => v.items)
		.map(x => x.date)
		.filter((date, i, self) => self.findIndex(d => d.getTime() === date.getTime()) === i)
		.sort((a, b) => a.getTime() - b.getTime());

	const allValues = allItems.map(x => x.value);
	const highestValue = allValues.length > 0 ? Math.max(...allValues) : 0;

	const toMonthKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}`;
	const monthBreakdown = new Map<string, { rows: { vehicleId: string; registrationNumber: string; value: number; color: string }[]; total: number }>();
	allDates.forEach(date => {
		const key = toMonthKey(date);
		if (monthBreakdown.has(key)) return;
		const rows = data.items.map((v, i) => ({
			vehicleId: v.vehicleId,
			registrationNumber: v.registrationNumber,
			value: v.items.find((m: FuelExpensesPerAssetMonthDto) => toMonthKey(m.date) === key)?.value ?? 0,
			color: colorForIndex(i),
		}));
		const total = rows.reduce((sum, r) => sum + r.value, 0);
		monthBreakdown.set(key, { rows, total });
	});

	const legendData = data.items.map((v, i) => ({
		name: v.registrationNumber,
		vehicleId: v.vehicleId,
		symbol: { fill: !hiddenVehicles.includes(v.vehicleId) ? colorForIndex(i) : Colors.gray17 },
		labels: { fill: Colors.primaryDark },
		clickable: true,
	}));

	const onClickLegend = (propsData: any) => {
		const vehicleId = propsData.datum.vehicleId;
		if (!vehicleId) return;
		setHiddenVehicles(prev => prev.includes(vehicleId) ? prev.filter(x => x !== vehicleId) : [...prev, vehicleId]);
		setReRenderKey(new Date().getTime());
	};

	const legendItemWidth = 130;
	const legendTotalWidth = legendData.length * legendItemWidth;
	const legendX = Math.max((chartWidth - legendTotalWidth) / 2, 0);

	const tooltipData = tooltip ? monthBreakdown.get(toMonthKey(new Date(tooltip.dateTime))) : null;

	return (
		<div className={styles.chartContainer} ref={containerRef}>
			{width > 0 && height > 0 && (
				<svg width={width} height={height} key={'svg-' + reRenderKey}>
					{visibleItems.length > 0 && (
						<VictoryChart
							standalone={false}
							width={chartWidth}
							height={chartHeight}
							padding={chartPadding}
							maxDomain={{ y: highestValue }}
							minDomain={{ y: 0 }}
						>
							<VictoryAxis
								dependentAxis
								standalone={false}
								tickValues={allValues}
								style={{
									axis: { stroke: 'transparent', fill: 'none' },
									ticks: { stroke: 'transparent', fill: 'none' },
									axisLabel: { stroke: 'transparent', fill: 'none' },
								}}
								tickLabelComponent={<ChartEmptyLabel />}
							/>
						</VictoryChart>
					)}

					{visibleItems.length > 0 && (
						<VictoryChart
							standalone={false}
							width={chartWidth}
							height={chartHeight}
							padding={chartPadding}
							scale={{ x: 'time' }}
							domainPadding={{ x: 30 }}
							key={'chart-' + reRenderKey}
						>
							{visibleItems.map(vehicle => {
								const colorIndex = data.items.findIndex(v => v.vehicleId === vehicle.vehicleId);
								const color = colorForIndex(colorIndex);
								return (
									<VictoryLine
										key={'line-' + vehicle.vehicleId}
										data={vehicle.items}
										x="date"
										y="value"
										interpolation="monotoneX"
										style={{ data: { stroke: color, strokeWidth: 2 } }}
									/>
								);
							})}
							{visibleItems.map(vehicle => {
								const colorIndex = data.items.findIndex(v => v.vehicleId === vehicle.vehicleId);
								const color = colorForIndex(colorIndex);
								const events: any[] = [
									{
										target: 'data',
										eventHandlers: {
											onMouseEnter: (evt: any, targetProps: any) => {
												setTooltip({
													dateTime: targetProps.datum.date.getTime(),
													clientX: evt.clientX,
													clientY: evt.clientY,
												});
												return [];
											},
											onMouseMove: (evt: any) => {
												setTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
												return [];
											},
											onMouseLeave: () => {
												setTooltip(null);
												return [];
											},
											onClick: (_evt: any, targetProps: any) => {
												goToCharges(vehicle.vehicleId, vehicle.registrationNumber, targetProps.datum.date);
												return [];
											},
										},
									},
								];
								return (
									<VictoryScatter
										key={'scatter-' + vehicle.vehicleId}
										data={vehicle.items.filter(d => d.value > 0)}
										x="date"
										y="value"
										size={4}
										style={{ data: { fill: color, cursor: 'pointer' } }}
										events={events}
									/>
								);
							})}
							<VictoryAxis
								dependentAxis
								style={{
									axis: { stroke: 'transparent' },
									grid: { stroke: '#f5f5f5' },
									tickLabels: { fontSize: 10, fontFamily: 'Roboto', fill: '#6b7280' },
								}}
								tickFormat={x => Utils.formatCurrency(x)}
							/>
							<VictoryAxis
								orientation="bottom"
								width={chartWidth}
								height={chartHeight}
								tickValues={allDates}
								tickFormat={x => Utils.capitalize(dayjs(x).locale(locale).format('MMM')) + ' ' + dayjs(x).format('YY')}
								style={{
									tickLabels: { fill: Colors.black, userSelect: 'none', fontSize: 12, fontFamily: 'Roboto' },
									axis: { stroke: Colors.gray3 },
								}}
							/>
						</VictoryChart>
					)}

					<VictoryLegend
						standalone={false}
						x={legendX}
						y={chartHeight - legendHeight}
						gutter={40}
						data={legendData}
						orientation={'horizontal'}
						itemsPerRow={Math.max(legendData.length, 1)}
						labelComponent={<ChartLegendLabel onClick={onClickLegend} />}
						style={{ labels: { fontSize: 12, userSelect: 'none', fontFamily: 'Roboto' } }}
					/>
				</svg>
			)}

			{tooltip && tooltipData && (
				<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
					{tooltipData.rows.map(row => (
						<div key={row.vehicleId} className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: row.color }} />
							<span>{row.registrationNumber}: {Utils.formatCurrency(row.value)}</span>
						</div>
					))}
					<div className={`${styles.tooltipRow} ${styles.tooltipTotal}`}>
						<span>{t('common.total' as any) as string}: {Utils.formatCurrency(tooltipData.total)}</span>
					</div>
				</ChartTooltipPortal>
			)}
		</div>
	);
};

export default FuelExpensesPerAssetChart;
